import { useMemo } from "react";

import { IconEyeglasses, IconSlidersHorizontal } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { isNullOrUndefined } from "utilities/object/object";
import { routes } from "utilities/routes";

import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";

import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { SubSetupTabKeys } from "entity/sub/SubSetup/SubSetup.api.types";

import "./NotificationPanelHeader.less";

interface INotificationPanelHeaderProps {
    selectedJobId: number;
    isLoading: boolean;
    onMarkAllReadAsync: () => Promise<void>;
    focusButtonRef?: React.Ref<HTMLElement>;
}

export function NotificationPanelHeader({
    selectedJobId,
    isLoading,
    onMarkAllReadAsync,
    focusButtonRef,
}: Readonly<INotificationPanelHeaderProps>) {
    const userInfo = useRequiredContext(UserInfoContext);

    const preferencesLink = useMemo(() => {
        if (isNullOrUndefined(userInfo.globalUserId)) {
            return null;
        }

        if (userInfo.loginType === BTLoginTypes.BUILDER) {
            return routes.internalUsers.getInternalUserDetailsLink(
                userInfo.globalUserId,
                InternalUserTabKeys.Notifications,
            );
        } else if (userInfo.loginType === BTLoginTypes.SUBS) {
            return routes.sub.getSetupLink(userInfo.globalUserId, SubSetupTabKeys.Notifications);
        } else if (userInfo.loginType === BTLoginTypes.CONTACT) {
            return routes.owner.getSetupLink(selectedJobId);
        }
        return null;
    }, [userInfo.globalUserId, userInfo.loginType, selectedJobId]);

    return (
        <BTRow
            data-testid="PanelTitle"
            className="NotificationPanelHeader"
            justify="center"
            align="middle"
        >
            <BTText text="Notifications" size="distinct-lg" className="flex-grow-1" />
            <BTPopover content="Mark all read" data-testid="markAllReadTooltip">
                <div className="margin-right-xs">
                    <BTButton
                        data-testid="markAllRead"
                        aria-label="Mark all read"
                        icon={<IconEyeglasses />}
                        onClick={onMarkAllReadAsync}
                        disabled={isLoading}
                        className="MarkAllReadButton HeaderButton"
                        type="tertiary"
                        ref={focusButtonRef}
                    />
                </div>
            </BTPopover>

            <BTPopover content="Preferences" data-testid="preferencesTooltip">
                <BTLinkRelative
                    to={`/NotificationPanel${preferencesLink}`}
                    className="NotificationLink"
                    isUnderline={false}
                >
                    <BTButton
                        data-testid="preferences"
                        type="tertiary"
                        aria-label="Preferences"
                        icon={<IconSlidersHorizontal />}
                        disabled={isLoading}
                        className="HeaderButton"
                    />
                </BTLinkRelative>
            </BTPopover>
        </BTRow>
    );
}
